import { Stack } from "@chakra-ui/react";
import { useEffect } from "react";

import { updateData } from "@ud/store/insertForm/insertSlice";
import { useOfferInsert } from "contexts/InsertOffer";
import useGlobalState from "hooks/useGlobalState";
import useRouter from "hooks/useRouter";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import { useLoginModal } from "modals/useLoginModal";
import { useTracking } from "contexts/Tracking";
import useHelperCRM from "hooks/useHelperCRM";
import { useLandlordData } from "components/LandlordForm";
import CustomSpinner from "components/CustomSpinner";
import ContactForm, { IFormValue } from "./ContactForm";

type IContact = {
  buttonNextText?: string;
  buttonPrevText?: string;
};

const ContactFormWrapper: React.FC<IContact> = ({
  buttonNextText,
  buttonPrevText,
}) => {
  const { user } = useGlobalState();
  const { openModal: openLoginModal } = useLoginModal();
  const dispatch = useAppDispatch();
  const insertReduxData = useAppSelector((state) => state.insertForm.data);
  const { navigateTo } = useRouter();
  const { trackEvent } = useTracking();
  const { createEventOfferDraft } = useHelperCRM();
  const { updateStep, nextStep } = useOfferInsert();

  const { landlordData, isDataLoaded, registerAndUpdateLandlord } =
    useLandlordData();

  const handleFormSubmit = async (formData: IFormValue) => {
    const {
      companyName,
      email,
      firstName,
      lastName,
      isSendNews,
      phone,
      typeInsert,
    } = formData;

    const proceedToNextStep = () => {
      dispatch(
        updateData({
          data: {
            email,
            lastName,
            companyName,
            typeInsert,
            firstName,
            isSendNews: user ? isSendNews : undefined,
            phone,
          },
        }),
      );
      updateStep("finished");
      trackEvent({
        "dl.eCat": "offer_create",
        "dl.eAct": "bttn.clicked",
        "dl.eLab": "1. krok – Kontakt",
      });
      const step = nextStep();
      navigateTo(step.url, {
        id: step.query?.id,
        status: step.query?.status,
        paidId: step.query?.paidId,
      });
    };

    if (!user) {
      return openLoginModal({
        initialEmail: email,
        onSuccessfulLogin: async () => {
          await registerAndUpdateLandlord(
            {
              firstName,
              lastName,
              phone,
              companyName,
              typeInsert,
            },
            {
              onAfterUpdate: proceedToNextStep,
            },
          );
        },
        loginEvent: {
          LOGIN: {
            "dl.eAct": "login",
            "dl.eLab": "Vložit inzerát",
            "dl.eCat": "",
          },
          REGISTRATION: {
            "dl.eCat": "",
            "dl.eAct": "registration",
            "dl.eLab": "Vložit inzerát",
          },
        },
      });
    }

    createEventOfferDraft();

    await registerAndUpdateLandlord(
      {
        firstName,
        lastName,
        phone,
        companyName,
        typeInsert,
      },
      {
        onAfterUpdate: proceedToNextStep,
      },
    );
  };

  useEffect(
    () =>
      trackEvent({
        "dl.eCat": "offer_create",
        "dl.eAct": "page.loaded",
        "dl.eLab": "1. krok – Kontakt",
      }),
    [trackEvent],
  );

  // Zobraz loader len pre prihlásených používateľov, kým sa načítajú ich dáta
  if (user && !isDataLoaded) {
    return (
      <Stack align="center" justify="center" minH="400px">
        <CustomSpinner />
      </Stack>
    );
  }

  return (
    <ContactForm
      buttonNextText={buttonNextText}
      buttonPrevText={buttonPrevText}
      landlordData={landlordData}
      insertReduxData={insertReduxData}
      onSubmit={handleFormSubmit}
      openLoginModal={openLoginModal}
    />
  );
};

export default ContactFormWrapper;
